import type { RuleConfiguratorProps } from "./types";
import React, { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { ArrowLongLeftIcon } from "@heroicons/react/24/solid";
import RadioGroup from "@ui-components/RadioGroup";

import {DATA_TRANSFER_ALLOWED_TYPES, LOGIC_DEPTYPE, LOGIC_VALTYPE} from "./lib/consts";
import {QuotationSurveyPrepopulationRuleSchema} from "@feature/types";
import SearchSelect from "@ui-components/SearchSelect";
import ConfiguratorSurveyQuestion from "@components/ConfiguratorSurveyQuestion";
import Label from "@ui-components/Label";
import Button from "@ui-components/Button";
import {PencilIcon, PlusIcon} from "@heroicons/react/24/outline";
import ServiceConfiguratorBlockExpressionConfigurator from "@components/ServiceConfiguratorBlockExpressionConfigurator";
import Select from "@ui-components/Select";
import {isDesignOutputSelectable} from "@feature/services/associate-services-logic/lib/utilities";
import _ from "lodash";

export function RuleConfigurator({
  question,
  questionOptions,
  onClose,
  onSave,
  rule = undefined,
  designServices,
  service
}: RuleConfiguratorProps) {

  // hooks
  const intl = useIntl();

  // state
  const [localRule, setLocalRule]
    = useState<QuotationSurveyPrepopulationRuleSchema>(rule || {} as QuotationSurveyPrepopulationRuleSchema);
  const [showDependenciesEditor, setShowDependenciesEditor]
    = useState(false);

  // utilities
  const depType = !!localRule?.dependencies ? "conditional" : "simple";
  const ConditionsEditIcon = !localRule?.dependencies?.length ? PlusIcon : PencilIcon;
  const designServicesOptions = designServices
    .map(({id, name}) => ({value: id, label: name}));
  const selectedDesignBlocks = designServices
    .find(({id}) => id === localRule?.id_design_service)
    ?.configurations
    ?? [];
  const selectableDesignOutputOptions = selectedDesignBlocks
    .filter((b) =>
      b.type === "output"
      && isDesignOutputSelectable(question.input_type, b, selectedDesignBlocks)
    )
    .map(({id, name}) =>
      ({value: id, label: name})
    );
  const validate = (r?: QuotationSurveyPrepopulationRuleSchema) => {
    if (!r || !r.id_design_service || !r.value_type || (!r.value && r.value !== false))
      return false;
    if (depType === "conditional" && !r.dependencies?.length)
      return false;
    return true;
  }
  
  if (showDependenciesEditor) {
    const designBlocks = designServices
      .find(({id}) => id === localRule?.id_design_service)
      ?.configurations
      ?? [];
    return (
      <ServiceConfiguratorBlockExpressionConfigurator
        setShowDependencies={setShowDependenciesEditor}
        fullExpression={localRule?.dependencies ?? []}
        newData={localRule}
        setNewData={setLocalRule}
        questions={designBlocks.filter(({type}) => type === "question")}
        recursiveQuestions={designBlocks.filter(({type}) => type === "recursive_question")}
        outputs={designBlocks.filter((b) =>
          b.type === "output"
          && ["calculation", "json_output_value"].includes(b.output_type || "")
        )}
        data={designBlocks}
        type="dependencies"
        lockMissingSelectedOptions
        block={question}
        service={service}
        fromDesignToQuotation={true}
      />
    )
  }

  return (
    <div className="h-full flex flex-col justify-between">

      {/* ******************** TOP SECTION ******************** */}
      <div>

        {/* ==================== Tab title + back button ==================== */}
        <div className="w-full flex mt-2 mb-4">
          <div className="flex flex-row gap-3 items-center">
            <ArrowLongLeftIcon
              className="cursor-pointer w-10"
              onClick={() => {
                onClose(_.isEqual(rule, localRule));
              }}
            />
            <h3 className="text-xl font-semibold">
              <FormattedMessage id={!!rule ? "edit_logic" : "add_logic"} />
            </h3>
          </div>
        </div>

        {/* ==================== Configuration ==================== */}
        <div className="flex flex-col gap-4">
          {/* -------------------- Logic type -------------------- */}
          <RadioGroup
            horizontal
            id="building-owner-type"
            title={intl.formatMessage({
              id: "logic_answer_type",
              defaultMessage: "Tipo di risposta",
            })}
            description={undefined}
            options={LOGIC_DEPTYPE.map((q) => ({
              name: intl.formatMessage({ id: q }),
              value: q,
            }))}
            currentValue={depType}
            onChange={(selected: string) => {
              const ruleWithCondition = {
                ...localRule,
                dependencies: selected === "conditional" ? [] : null,
              } as QuotationSurveyPrepopulationRuleSchema;
              setLocalRule(ruleWithCondition);
            }}
          />

          {/* -------------------- Logic target service -------------------- */}
          {/*@ts-ignore*/}
          <SearchSelect
            label={intl.formatMessage({
              id: "logic_target_design_service",
              defaultMessage: "Progettazione di riferimento",
            })}
            options={designServicesOptions}
            onChange={({value}: {value: number}) => {
              setLocalRule((prev) => ({
                ...(prev || {} as QuotationSurveyPrepopulationRuleSchema),
                id_design_service: value,
              }));
            }}
            value={designServicesOptions.find(({value}) => value === localRule?.id_design_service)}
          />

          {!!localRule?.id_design_service && <>

            {/* -------------------- Logic value type -------------------- */}
            {/*@ts-ignore*/}
            <Select
              label={intl.formatMessage({
                id: "logic_value_type",
                defaultMessage: "Tipo di valore",
              })}
              options={[
                {name: "", value: ""},
                ...LOGIC_VALTYPE.map((lvt: "fixed" | "dynamic") => ({
                  name: intl.formatMessage({id: `logic_value_type_${lvt}`}),
                  value: lvt,
                  disabled: lvt === "dynamic"
                    && !!question.input_type
                    && !DATA_TRANSFER_ALLOWED_TYPES.includes(question.input_type),
                }))
              ]}
              value={localRule?.value_type}
              onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                setLocalRule((prev) => ({
                  ...(prev || {} as QuotationSurveyPrepopulationRuleSchema),
                  value_type: e.target.value as "fixed" | "dynamic",
                }));
              }}
            />

            {/* -------------------- Logic value -------------------- */}
            {localRule?.value_type === "fixed" && (
              <div>
                <Label>
                  <FormattedMessage id="value" defaultMessage="Valore" />
                </Label>
                {/*@ts-ignore*/}
                <ConfiguratorSurveyQuestion
                  question={{...question, options: questionOptions}}
                  value={localRule?.value ?? (question.type === "bool" ? true : undefined)}
                  onChange={(value: any) => {
                    setLocalRule((prev) => ({
                      ...(prev || {} as QuotationSurveyPrepopulationRuleSchema),
                      value,
                    }));
                  }}
                  canonicalRender
                />
              </div>
            )}
            {localRule?.value_type === "dynamic" && <>
              <div>
                <Label>
                  <FormattedMessage id="select_output" defaultMessage="Seleziona un output di progettazione" />
                </Label>
                {/*@ts-ignore*/}
                <SearchSelect
                  options={selectableDesignOutputOptions}
                  value={selectableDesignOutputOptions.find(({value}) => value == localRule?.value)}
                  onChange={({value}: {value: number}) => {
                    setLocalRule((prev) => ({
                      ...(prev || {} as QuotationSurveyPrepopulationRuleSchema),
                      value,
                    }));
                  }}
                />
              </div>
            </>}

            {/* -------------------- Logic dependencies -------------------- */}
            {!!localRule?.dependencies && (
              <div className="w-full flex flex-row justify-between items-center">
                <Label>
                  <FormattedMessage id="conditions" defaultMessage="Condizioni" />
                </Label>
                <ConditionsEditIcon
                  className="w-6 cursor-pointer"
                  onClick={() => { setShowDependenciesEditor(true); }}
                />
              </div>
            )}

          </>}

        </div>
      </div>

      {/* ******************** BOTTOM SECTION ******************** */}
      <div className="flex flex-row justify-end">
        {/* ==================== Save button ==================== */}
        {/*@ts-ignore*/}
        <Button
          styleType="darkGreen"
          onClick={() => { onSave(localRule); }}
          disabled={!validate(localRule)}
        >
          <FormattedMessage id="confirm" />
        </Button>
      </div>

    </div>
  );
}
